import { memo, useEffect } from 'react'
import { ShareIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { toast } from '../../services/toastService'
import { createLogger } from '../../utils/logger'

const logger = createLogger('SightingShareCard')

/**
 * SightingShareCard - Post-submit share prompt floating above the tab bar
 *
 * Auto-dismisses after 8 seconds. Invokes native share sheet when available,
 * falls back to clipboard copy.
 *
 * @param {Object} props
 * @param {string} props.sightingId - UUID of the newly created sighting
 * @param {string} props.entityName - Name of the spotted entity
 * @param {Function} props.onDismiss - Callback to clear the card
 * @param {Function} props.t - Translation function
 *
 * @atomic - Single purpose: share prompt after sighting submit
 * @idempotent - Pure component, same inputs = same output
 */
const SightingShareCard = memo(function SightingShareCard({
  sightingId,
  entityName,
  onDismiss,
  t
}) {
  // Auto-dismiss after 8 seconds
  useEffect(() => {
    const timer = setTimeout(onDismiss, 8000)
    return () => clearTimeout(timer)
  }, [onDismiss])

  const shareUrl = `${window.location.origin}/sightings/${sightingId}`

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: entityName,
          text: t('spotMode.shareCard.title', 'Sighting submitted!'),
          url: shareUrl
        })
      } else {
        await navigator.clipboard.writeText(shareUrl)
        toast.success(t('common.linkCopied', 'Link copied!'))
      }
    } catch (err) {
      // User cancelled the share dialog — not an error
      if (err.name !== 'AbortError') {
        logger.warn('Share failed', err)
        // Clipboard fallback
        try {
          await navigator.clipboard.writeText(shareUrl)
          toast.success(t('common.linkCopied', 'Link copied!'))
        } catch (clipErr) {
          logger.error('Clipboard fallback failed', clipErr)
        }
      }
    }
  }

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[997] w-[min(22rem,calc(100vw-2rem))]"
      style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}
    >
      <div className="bg-white border border-primary/20 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3">
        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {t('spotMode.shareCard.title', 'Sighting submitted!')}
          </p>
          <p className="text-xs text-gray-500 truncate">{entityName}</p>
        </div>

        {/* Share button */}
        <button
          onClick={handleShare}
          onTouchEnd={(e) => { e.preventDefault(); handleShare() }}
          className="flex items-center gap-1.5 bg-primary hover:bg-primary-600 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
        >
          <ShareIcon className="h-4 w-4" />
          <span>{t('spotMode.shareCard.share', 'Share')}</span>
        </button>

        {/* Dismiss button */}
        <button
          onClick={onDismiss}
          onTouchEnd={(e) => { e.preventDefault(); onDismiss() }}
          className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors flex-shrink-0"
          aria-label={t('spotMode.shareCard.dismiss', 'Dismiss')}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
})

export default SightingShareCard
